import type { JSX } from 'react';

import { Col, Row, Form } from 'antd';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { resetPassword } from 'src/actions';
import { MInputPassword, MButton, MTypography, PasswordStrengthBar, CardLayout } from 'src/lib';
import { Color, Image, Spacing } from 'src/styles';

import * as Styles from '../BaseLayout.styles';

import { newPasswordValidationSchema } from './validations';

interface ResetPasswordFormValues {
  password: string;
  confirmPassword: string;
}

export const ResetPassword = (): JSX.Element => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const isSucceeded = useSelector((state: any) => Boolean(state.user.resetPassword.__succeeded));
  const isRequested = useSelector((state: any) => Boolean(state.user.resetPassword.__requested));

  const email = searchParams.get('email');
  const code = searchParams.get('code');
  const firstName = searchParams.get('firstName') ?? '';
  const lastName = searchParams.get('lastName') ?? '';

  const renderResetPasswordForm = () => {
    return (
      <Formik<ResetPasswordFormValues>
        initialValues={{
          password: '',
          confirmPassword: '',
        }}
        validationSchema={newPasswordValidationSchema}
        onSubmit={values => {
          dispatch(resetPassword({ code, email, password: values.password }));
        }}>
        {form => {
          return (
            <Form autoComplete='off' style={{ width: '100%' }}>
              <Row justify='center'>
                <MTypography variant='h6' weight='bold' color={Color.Gray9} className={Styles.formTitle}>
                  Forgot Password
                </MTypography>
              </Row>
              <Row>
                <Col span={24} className={Spacing.my16}>
                  <MInputPassword
                    placeholder='New Password'
                    onChange={(value: string) => form.setFieldValue('password', value)}
                    value={form.values.password}
                    error={form.errors.password}
                    name='reset-password-new-password-name-attribute'
                    autoComplete='new-password'
                  />
                </Col>
                <Col span={24} className={Spacing.my16}>
                  <MInputPassword
                    placeholder='Confirm New Password'
                    onChange={(value: string) => form.setFieldValue('confirmPassword', value)}
                    value={form.values.confirmPassword}
                    error={form.errors.confirmPassword}
                    name='reset-password-confirm-password-name-attribute'
                    autoComplete='new-password'
                  />
                </Col>
                <Col span={24}>
                  <span>*New password needs to be different from a previously used password.</span>
                </Col>
                <Col span={24}>
                  <PasswordStrengthBar
                    password={form.values.password}
                    confirmPassword={form.values.confirmPassword}
                    firstName={firstName}
                    lastName={lastName}
                    showStrengthLabel
                    showValidationRules
                  />
                </Col>
              </Row>
              <Row justify='center' className={Spacing.my16}>
                <MButton
                  type='secondary'
                  loading={isRequested}
                  disabled={!form.isValid}
                  onClick={() => {
                    form.submitForm();
                  }}>
                  Save
                </MButton>
              </Row>
            </Form>
          );
        }}
      </Formik>
    );
  };

  const renderResetPasswordSubmitted = () => {
    return (
      <>
        <Row justify='center'>
          <div className={Styles.iconContainer}>
            <img src={Image.Completed} alt='Completed' />
          </div>
        </Row>
        <Row justify='center'>
          <MTypography
            variant='subtitle1'
            weight='bold'
            align='center'
            color={Color.Green7}
            className={Styles.afterActionTitle}>
            Password Changed!
          </MTypography>
        </Row>
        <Row justify='center'>
          <MTypography variant='body1' align='center' color={Color.Gray7} className={Styles.afterActionSubtitle}>
            Your password has been successfully changed.
          </MTypography>
        </Row>

        <Row justify='center'>
          <MButton
            type='secondary'
            onClick={() => {
              navigate('/login');
            }}>
            Go To Log In
          </MButton>
        </Row>
      </>
    );
  };

  return <CardLayout>{!isSucceeded ? renderResetPasswordForm() : renderResetPasswordSubmitted()}</CardLayout>;
};
